import { useState, useEffect } from "react";
import { Grid, Box } from "@material-ui/core";
import AppBar from "@material-ui/core/AppBar";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import { useTranslation } from "react-i18next";

import { AgroAnalyticsPerilsList } from "./AgroAnalyticsPerilsList";

import { AgroRiskLevelGrid } from "./AgroRiskLevelGrid";
import { AgroBivariateGrid } from "./AgroBivariateGrid";
import { AgroCoverageGrid } from "./AgroCoverageGrid";
import { AgroProvinceGrid } from "./AgroProvinceGrid";
import { AgroCommuneGrid } from "./AgroCommuneGrid";
import { AgroBivariateLegend } from "./AgroBivariateLegend";
import { UniversalMap } from "../map/UniversalMap";
import SelectReferenceDate from "../util/SelectReferenceDate";

import { AgroMacroSpecieGrid } from "./AgroMacroSpecieGrid";
import { AgroSpecieGroupGrid } from "./AgroSpecieGroupGrid";
import { AgroSpecieGrid } from "./AgroSpecieGrid";
import { saveUserMonitorData } from "../util/userUtils";

export const AgroAnalyticsPage = () => {
  const { t } = useTranslation();

  const [filterModel, setFilterModel] = useState({});
  const [filteredMapData, setFilteredMapData] = useState(null);

  const [hazardSelect, setHazardSelect] = useState("anyhazard");
  const [KPISelect, setKPISelect] = useState("none");
  const [selectedComuneIstat, setSelectedComuneIstat] = useState(null);
  const [requestedSubpageTabIdx, setRequestedSubpageTabIdx] = useState(0);
  const [requestedTabIdx, setRequestedTabIdx] = useState(0);

  const [wantedReferenceDate, setWantedReferenceDate] = useState(null);
  const [listOfValidReferenceDates, setListOfValidReferenceDates] = useState([
    // To be loaded by the backend from catalog.
    "202212",
  ]);

  useEffect(() => {
    const getCatalog = async () => {
      saveUserMonitorData("agroAnalytics", "");

      const allReferenceDatesInCatalog = ["2019", "2020", "2021", "2022", "2023", "2024"];
      if (Array.isArray(allReferenceDatesInCatalog)) {
        setListOfValidReferenceDates(allReferenceDatesInCatalog.reverse());

        setWantedReferenceDate(allReferenceDatesInCatalog[2]);
      }
    };
    getCatalog();
  }, []);

  const handleSubpageChange = (event, newValue) => {
    setRequestedSubpageTabIdx(newValue);
    if (newValue === 1 && requestedTabIdx > 2) {
      setRequestedTabIdx(0);
    }
  };

  const handleChange = (event, newRequestedTabIdx) => {
    setRequestedTabIdx(newRequestedTabIdx);
    saveUserMonitorData("agroAnalytics", `${newRequestedTabIdx}`);
  };

  const handleWantedReferenceDateChange = (event) => {
    setWantedReferenceDate(event.target.value);
  };

  return (
    wantedReferenceDate && (
      <Grid style={{ marginTop: 25 }}>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={12}>
            <UniversalMap
              page="buildings-policies"
              filteredMapDataProp={filteredMapData}
              hazardSelect={hazardSelect}
              setHazardSelect={setHazardSelect}
              KPISelect={KPISelect}
              setKPISelect={setKPISelect}
              variantProp="AgroClimateAnalytics"
              routeNameProp="policies"
              setFilteredMapDataProp={setFilteredMapData}
              filterModel={filterModel}
              wantedReferenceDate={wantedReferenceDate}
              selectedComuneIstatProp={selectedComuneIstat}
            />
          </Grid>
          <SelectReferenceDate
            wantedReferenceDate={wantedReferenceDate}
            handleWantedReferenceDateChange={handleWantedReferenceDateChange}
            listOfValidReferenceDates={listOfValidReferenceDates}
          />
          <AgroAnalyticsPerilsList
            filterModel={filterModel}
            setFilterModel={setFilterModel}
            setFilteredMapData={setFilteredMapData}
            wantedReferenceDateProp={wantedReferenceDate}
          ></AgroAnalyticsPerilsList>

          <Box display="flex" flexDirection="column">
            <Box p={1}>
              <AppBar position="static" color="default" elevation={0}>
                <Tabs
                  value={requestedSubpageTabIdx}
                  textColor="primary"
                  indicatorColor="primary"
                  onChange={handleSubpageChange}
                >
                  <Tab label={t("climateAnalyticsTabs.portfolioStatistics")} />

                  <Tab label={t("climateAnalyticsTabs.specificHazards")} />
                </Tabs>
              </AppBar>
            </Box>
            {requestedSubpageTabIdx === 0 && (
              <Box p={1}>
                <AppBar position="static" color="default" elevation={0}>
                  <Tabs value={requestedTabIdx} onChange={handleChange}>
                    <Tab label={t("climateAnalyticsTabs.coverages")} />
                    <Tab label={"Specie"} />
                    <Tab label={"Macrospecie"} />
                    <Tab label={"Specie Group"} />
                    <Tab label={t("climateAnalyticsTabs.provinces")} />
                    <Tab label={t("climateAnalyticsTabs.municipalities")} />
                  </Tabs>
                </AppBar>
              </Box>
            )}

            {requestedSubpageTabIdx === 1 && (
              <Box p={1}>
                <AppBar position="static" color="default" elevation={0}>
                  <Tabs value={requestedTabIdx} onChange={handleChange}>
                    <Tab label={t("climateAnalyticsTabs.riskLevels")} />
                    <Tab label={t("climateAnalyticsTabs.legend")} />
                    <Tab label={t("climateAnalyticsTabs.bivariate")} />
                  </Tabs>
                </AppBar>
              </Box>
            )}
          </Box>
          {requestedSubpageTabIdx === 0 && requestedTabIdx === 0 && (
            <AgroCoverageGrid filterModel={filterModel} wantedReferenceDateProp={wantedReferenceDate} />
          )}
          {requestedSubpageTabIdx === 0 && requestedTabIdx === 1 && (
            <AgroSpecieGrid filterModel={filterModel} wantedReferenceDateProp={wantedReferenceDate} />
          )}
          {requestedSubpageTabIdx === 0 && requestedTabIdx === 2 && (
            <AgroMacroSpecieGrid filterModel={filterModel} wantedReferenceDateProp={wantedReferenceDate} />
          )}
          {requestedSubpageTabIdx === 0 && requestedTabIdx === 3 && (
            <AgroSpecieGroupGrid filterModel={filterModel} wantedReferenceDateProp={wantedReferenceDate} />
          )}
          {requestedSubpageTabIdx === 0 && requestedTabIdx === 4 && (
            <AgroProvinceGrid filterModel={filterModel} wantedReferenceDateProp={wantedReferenceDate} />
          )}
          {requestedSubpageTabIdx === 0 && requestedTabIdx === 5 && (
            <AgroCommuneGrid
              filterModel={filterModel}
              wantedReferenceDateProp={wantedReferenceDate}
              setSelectedComuneIstatSetter={setSelectedComuneIstat}
            />
          )}
          {requestedSubpageTabIdx === 1 && requestedTabIdx === 0 && (
            <AgroRiskLevelGrid
              filterModel={filterModel}
              hazardSelect={hazardSelect}
              KPISelect={KPISelect}
              wantedReferenceDateProp={wantedReferenceDate}
            />
          )}
          {requestedSubpageTabIdx === 1 && requestedTabIdx === 1 && (
            <AgroBivariateLegend
              hazardSelect={hazardSelect}
              KPISelect={KPISelect}
              wantedReferenceDateProp={wantedReferenceDate}
            />
          )}
          {requestedSubpageTabIdx === 1 && requestedTabIdx === 2 && (
            <AgroBivariateGrid
              filterModel={filterModel}
              hazardSelect={hazardSelect}
              KPISelect={KPISelect}
              wantedReferenceDateProp={wantedReferenceDate}
              setSelectedComuneIstatSetter={setSelectedComuneIstat}
            />
          )}
        </Grid>
      </Grid>
    )
  );
};
